import { Message } from '../types/chat';

// Constants
const HISTORY_SIZE_LIMIT = 20; // Maximum number of messages to store per client
const HISTORY_STORAGE_KEY = 'chat_history';

// Type definitions
interface StreamInfo {
  content: string;
  isStreaming: boolean;
  metadata?: any;
  lastUpdated: number;
  userQuery?: string;
  lastSavedAt?: number;
  retryCount?: number;
}

interface ChatHistoryRecord {
  clientId: string;
  messages: Message[];
  lastUpdated: number;
  activeStreams: {
    [messageId: string]: StreamInfo;
  };
}

// Main service functions
export const chatHistoryService = {
  // Save messages to local storage
  saveHistory(clientId: string, messages: Message[]): void {
    try {
      let histories: ChatHistoryRecord[] = this.getAllHistories();
      
      // Find existing history for this client
      const existingIndex = histories.findIndex(h => h.clientId === clientId);
      
      if (existingIndex >= 0) {
        // Update existing history while preserving active streams
        histories[existingIndex] = {
          ...histories[existingIndex],
          clientId,
          messages: messages.slice(-HISTORY_SIZE_LIMIT),
          lastUpdated: Date.now()
        };
      } else {
        // Add new history
        histories.push({
          clientId,
          messages: messages.slice(-HISTORY_SIZE_LIMIT),
          lastUpdated: Date.now(),
          activeStreams: {}
        });
      }
      
      // Sort by most recently updated
      histories.sort((a, b) => b.lastUpdated - a.lastUpdated);
      
      // Store back to localStorage
      localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(histories));
    } catch (error) {
      console.error('Error saving chat history:', error);
    }
  },
  
  // Track streaming message
  trackStreamingMessage(clientId: string, messageId: string, content: string, userQuery: string, metadata?: any): void {
    try {
      let histories = this.getAllHistories();
      const existingIndex = histories.findIndex(h => h.clientId === clientId);
      
      if (existingIndex >= 0) {
        // Ensure activeStreams exists
        if (!histories[existingIndex].activeStreams) {
          histories[existingIndex].activeStreams = {};
        }
        
        const existingStream = histories[existingIndex].activeStreams[messageId];
        
        // Update stream info
        histories[existingIndex].activeStreams[messageId] = {
          content,
          isStreaming: true,
          metadata,
          lastUpdated: Date.now(),
          lastSavedAt: Date.now(),
          userQuery,
          retryCount: existingStream ? (existingStream.retryCount || 0) + 1 : 0
        };
        
        // Update history record
        histories[existingIndex].lastUpdated = Date.now();
        
        localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(histories));
        
        // Dispatch storage event for cross-tab sync
        window.dispatchEvent(new Event('storage'));
      }
    } catch (error) {
      console.error('Error tracking streaming message:', error);
    }
  },
  
  // Update streaming message
  updateStreamingMessage(clientId: string, messageId: string, content: string, metadata?: any): void {
    try {
      let histories = this.getAllHistories();
      const existingIndex = histories.findIndex(h => h.clientId === clientId);
      
      if (existingIndex >= 0) {
        // Ensure activeStreams exists
        if (!histories[existingIndex].activeStreams) {
          histories[existingIndex].activeStreams = {};
        }

        const activeStreams = histories[existingIndex].activeStreams;
        
        if (activeStreams[messageId]) {
          // Update stream content while preserving other fields
          activeStreams[messageId] = {
            ...activeStreams[messageId],
            content,
            metadata: { ...activeStreams[messageId].metadata, ...metadata },
            lastUpdated: Date.now()
          };
          
          localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(histories));
          
          // Dispatch storage event for cross-tab sync
          window.dispatchEvent(new Event('storage'));
        }
      }
    } catch (error) {
      console.error('Error updating streaming message:', error);
    }
  },
  
  // Complete streaming message
  completeStreamingMessage(clientId: string, messageId: string): void {
    try {
      let histories = this.getAllHistories();
      const existingIndex = histories.findIndex(h => h.clientId === clientId);
      
      if (existingIndex >= 0) {
        const activeStreams = histories[existingIndex].activeStreams;
        
        if (activeStreams && messageId in activeStreams) {
          // Remove completed stream using type-safe approach
          const { [messageId]: completedStream, ...remainingStreams } = activeStreams;
          histories[existingIndex].activeStreams = remainingStreams;
          
          // Add the completed message to history if not already present
          const messageExists = histories[existingIndex].messages.some(m => m.id === messageId);
          if (!messageExists && completedStream) {
            histories[existingIndex].messages.push({
              id: messageId,
              content: completedStream.content,
              citation_sources: completedStream.metadata?.citation_sources,
              isBot: true,
              role: 'assistant',
              metadata: completedStream.metadata,
              type: 'text'
            });
            
            // Also add the user query if available
            if (completedStream.userQuery) {
              const userMessageId = `user-${messageId}`;
              if (!histories[existingIndex].messages.some(m => m.id === userMessageId)) {
                histories[existingIndex].messages.push({
                  id: userMessageId,
                  content: completedStream.userQuery,
                  isBot: false,
                  role: 'user',
                  type: 'text'
                });
              }
            }
          }
          
          localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(histories));
          
          // Dispatch storage event for cross-tab sync
          window.dispatchEvent(new Event('storage'));
        }
      }
    } catch (error) {
      console.error('Error completing streaming message:', error);
    }
  },
  
  // Get active streams for a client
  getActiveStreams(clientId: string): { [messageId: string]: StreamInfo } {
    try {
      const histories = this.getAllHistories();
      const clientHistory = histories.find(h => h.clientId === clientId);
      
      if (clientHistory?.activeStreams) {
        // Clean up any stale streams (older than 1 hour)
        const now = Date.now();
        const activeStreams = { ...clientHistory.activeStreams };
        let hasStaleStreams = false;
        
        Object.entries(activeStreams).forEach(([messageId, stream]) => {
          // Check if stream is stale or has too many retries
          if (now - (stream.lastUpdated || 0) > 3600000 || // 1 hour
              (stream.retryCount || 0) > 3) { // Max 3 retries
            delete activeStreams[messageId];
            hasStaleStreams = true;
          }
        });
        
        // If we cleaned up any streams, update storage
        if (hasStaleStreams) {
          clientHistory.activeStreams = activeStreams;
          this.saveHistory(clientId, clientHistory.messages);
        }
        
        return activeStreams;
      }
      
      return {};
    } catch (error) {
      console.error('Error getting active streams:', error);
      return {};
    }
  },
  
  // Get history for a specific client
  getHistory(clientId: string): Message[] {
    try {
      const histories = this.getAllHistories();
      const clientHistory = histories.find(h => h.clientId === clientId);
      return clientHistory ? clientHistory.messages : [];
    } catch (error) {
      console.error('Error getting chat history:', error);
      return [];
    }
  },
  
  // Get all histories from storage
  getAllHistories(): ChatHistoryRecord[] {
    try {
      const storedData = localStorage.getItem(HISTORY_STORAGE_KEY);
      const histories = storedData ? JSON.parse(storedData) : [];
      
      // Ensure each record has activeStreams property
      return histories.map((history: any) => ({
        ...history,
        activeStreams: history.activeStreams || {}
      }));
    } catch (error) {
      console.error('Error parsing chat histories:', error);
      return [];
    }
  },
  
  // Clear chat history for a specific client
  clearHistory(clientId: string): void {
    try {
      let histories = this.getAllHistories();
      histories = histories.filter(h => h.clientId !== clientId);
      localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(histories));
      
      // Verify the clear worked
      const remainingForClient = this.getAllHistories().filter(h => h.clientId === clientId);
      if (remainingForClient.length > 0) {
        console.warn(`[ChatHistoryService] Warning: Still found ${remainingForClient.length} records for client ${clientId} after clearing`);
      } else {
        console.log(`[ChatHistoryService] Verified: No records remain for client ${clientId}`);
      }
    } catch (error) {
      console.error(`[ChatHistoryService] Error clearing chat history for client ${clientId}:`, error);
      
      // Attempt fallback direct clear in case of parsing error
      try {
        console.log(`[ChatHistoryService] Attempting fallback direct clear of localStorage`);
        localStorage.removeItem(HISTORY_STORAGE_KEY);
        console.log(`[ChatHistoryService] Removed all chat history via fallback method`);
      } catch (fallbackError) {
        console.error(`[ChatHistoryService] Fallback clear also failed:`, fallbackError);
      }
    }
  },
};